import { useState, useMemo, useEffect, useCallback } from 'react';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { saRoles, saRolesPermissionsState, type SaRole } from '@/mocks/saRolesPermissions';
import RoleStatsBar from './components/RoleStatsBar';
import RoleFilters from './components/RoleFilters';
import RoleTable from './components/RoleTable';
import RoleDetailModal from './components/RoleDetailModal';
import RoleFormModal from './components/RoleFormModal';

type LoadingState = 'loading' | 'ready' | 'error';

interface FormData {
  name: string;
  slug: string;
  description: string;
  icon: string;
  color: string;
  hierarchyLevel: number;
  active: boolean;
}

function simulateLoad(): Promise<SaRole[]> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.96) {
        reject(new Error('Error al conectar con el Permission Engine. Verificá la conexión.'));
      } else {
        resolve(saRoles);
      }
    }, 400 + Math.random() * 250);
  });
}

export default function SARolesPermissions() {
  const [roles, setRoles] = useState<SaRole[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<SaRole | null>(null);

  // Modal states
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit' | 'duplicate'; role: SaRole | null } | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStatus, setActiveStatus] = useState('all');
  const [activeHierarchy, setActiveHierarchy] = useState('all');

  const loadData = useCallback(() => {
    setLoadingState('loading');
    setErrorMsg(null);
    simulateLoad()
      .then((data) => {
        setRoles(data);
        setLoadingState('ready');
      })
      .catch((err) => {
        setErrorMsg(err.message || 'Error desconocido');
        setLoadingState('error');
      });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Show toast auto-hide
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // Counts
  const statusCounts = useMemo(() => {
    const c: Record<string, number> = { all: roles.length };
    roles.forEach((r) => {
      const k = r.active ? 'active' : 'disabled';
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [roles]);

  // Filtered
  const filteredRoles = useMemo(() => {
    let result = roles;
    if (activeStatus === 'active') result = result.filter((r) => r.active);
    if (activeStatus === 'disabled') result = result.filter((r) => !r.active);
    if (activeHierarchy !== 'all') {
      const lvl = parseInt(activeHierarchy);
      result = result.filter((r) => r.hierarchyLevel === lvl);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      result = result.filter((r) => r.name.toLowerCase().includes(q) || r.description.toLowerCase().includes(q) || r.slug.toLowerCase().includes(q));
    }
    result.sort((a, b) => a.hierarchyLevel - b.hierarchyLevel);
    return result;
  }, [roles, activeStatus, activeHierarchy, searchTerm]);

  // Handlers
  const handleSelect = (role: SaRole) => setSelectedRole(role);

  const handleEdit = (role: SaRole) => setFormModal({ mode: 'edit', role });

  const handleDuplicate = (role: SaRole) => setFormModal({ mode: 'duplicate', role });

  const handleToggleActive = (role: SaRole) => {
    setRoles((prev) => prev.map((r) => (r.id === role.id ? { ...r, active: !r.active } : r)));
    setToast(`Rol "${role.name}" ${role.active ? 'desactivado' : 'activado'} correctamente.`);
  };

  const handleSave = (data: FormData) => {
    if (formModal?.mode === 'create') {
      const newRole: SaRole = {
        id: `role-custom-${Date.now()}`,
        name: data.name,
        slug: data.slug,
        description: data.description,
        icon: data.icon,
        color: data.color,
        hierarchyLevel: data.hierarchyLevel,
        active: data.active,
        isSystem: false,
        createdAt: new Date().toISOString().split('T')[0],
        updatedAt: new Date().toISOString().split('T')[0],
        permissions: [],
        overrides: [],
        changeLog: [],
        userCount: 0,
        criticalPermissions: [],
      };
      setRoles((prev) => [...prev, newRole]);
      setToast(`Rol "${data.name}" creado correctamente.`);
    } else if (formModal?.role) {
      setRoles((prev) =>
        prev.map((r) =>
          r.id === formModal.role!.id
            ? { ...r, ...data, updatedAt: new Date().toISOString().split('T')[0] }
            : r,
        ),
      );
      setToast(`Rol "${data.name}" actualizado correctamente.`);
    }
  };

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1800px] mx-auto">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Roles and Permissions Center</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready'
                    ? `${filteredRoles.length} de ${roles.length} roles · ${saRolesPermissionsState.totalPermissionsConfigured} permisos configurados`
                    : 'Cargando roles...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setFormModal({ mode: 'create', role: null })}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors whitespace-nowrap"
                >
                  <i className="ri-add-line text-sm"></i>
                  Crear Rol
                </button>
                <button
                  onClick={loadData}
                  className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-500 text-xs font-medium cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Refrescar
                </button>
              </div>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2.5">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div key={i} className="bg-background-50 border border-background-200 rounded-2xl p-3.5">
                      <div className="h-3 bg-background-200 rounded w-16 mb-2 animate-pulse"></div>
                      <div className="h-5 bg-background-200 rounded w-8 animate-pulse"></div>
                    </div>
                  ))}
                </div>
                <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1200px]">
                      <thead>
                        <tr className="border-b border-background-200 bg-background-100/50">
                          {['Rol', 'Nivel', 'Tipo', 'Estado', 'Usuarios', 'Permisos', 'Críticos', 'Actualización', ''].map((h) => (
                            <th key={h} className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from({ length: 8 }).map((_, i) => (
                          <tr key={i} className="border-b border-background-200/60">
                            {Array.from({ length: 9 }).map((_, j) => (
                              <td key={j} className="px-4 py-3">
                                <div className="h-3 bg-background-200 rounded animate-pulse" style={{ width: `${40 + Math.random() * 70}px` }}></div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-rose-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-rose-400 text-2xl"></i>
                </div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar roles</p>
                <p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button
                  onClick={loadData}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold cursor-pointer hover:bg-foreground-800 transition-colors"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Reintentar
                </button>
              </div>
            )}

            {/* Stats */}
            {loadingState === 'ready' && <RoleStatsBar roles={roles} />}

            {/* Info banner */}
            {loadingState === 'ready' && (
              <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0">
                    <i className="ri-shield-keyhole-line text-accent-500 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-accent-800 text-xs font-semibold">Roles and Permissions Center</p>
                    <p className="text-accent-600/80 text-[10px]">Gestión centralizada de roles, permisos y accesos críticos. Preparado para integración con Auth, RLS y Supabase.</p>
                  </div>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-accent-200 text-accent-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 self-start sm:self-auto">
                  <i className="ri-database-2-line text-[9px]"></i>
                  Mock Data — Listo para conectar
                </span>
              </div>
            )}

            {/* Filters */}
            {loadingState === 'ready' && (
              <RoleFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                activeStatus={activeStatus}
                onStatusChange={setActiveStatus}
                activeHierarchy={activeHierarchy}
                onHierarchyChange={setActiveHierarchy}
                statusCounts={statusCounts}
              />
            )}

            {/* Table */}
            {loadingState === 'ready' && (
              <RoleTable
                roles={filteredRoles}
                onSelect={handleSelect}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onToggleActive={handleToggleActive}
              />
            )}
          </div>
        </main>
      </div>

      {/* Detail modal */}
      <RoleDetailModal
        role={selectedRole}
        onClose={() => setSelectedRole(null)}
        onEdit={(role) => { setSelectedRole(null); setFormModal({ mode: 'edit', role }); }}
      />

      {/* Form modal */}
      {formModal && (
        <RoleFormModal
          mode={formModal.mode}
          role={formModal.role}
          onSave={handleSave}
          onClose={() => setFormModal(null)}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[200] px-5 py-3 rounded-2xl bg-foreground-950 text-background-50 text-sm font-semibold shadow-xl animate-bounce">
          {toast}
        </div>
      )}

      {/* Mobile hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">
          Super Admin · Roles and Permissions Center
        </p>
      </div>
    </div>
  );
}